import React, { useEffect } from 'react';
import { X, AlertTriangle, AlertCircle, Info } from 'lucide-react';
import { ConfirmationDialog as ConfirmationDialogType } from '@/contexts/ToastContext';

interface ConfirmationDialogProps {
  dialog: ConfirmationDialogType;
  onClose: () => void;
}

export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({ dialog, onClose }) => {
  console.log('🎭 ConfirmationDialog renderizado:', dialog);

  useEffect(() => {
    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const getIcon = () => {
    switch (dialog.type) {
      case 'danger':
        return <AlertTriangle className="h-6 w-6 text-red-600" />;
      case 'warning':
        return <AlertTriangle className="h-6 w-6 text-yellow-600" />;
      case 'info':
        return <Info className="h-6 w-6 text-blue-600" />;
      default:
        return <AlertCircle className="h-6 w-6 text-brand-orange" />;
    }
  };

  const getButtonStyles = () => {
    switch (dialog.type) {
      case 'danger':
        return {
          confirm: 'bg-red-600 hover:bg-red-700 text-white border-red-600',
          cancel:
            'bg-brand-white text-brand-gray-dark border-brand-gray-dark hover:bg-brand-gray-dark hover:text-brand-white',
        };
      case 'warning':
        return {
          confirm: 'bg-yellow-600 hover:bg-yellow-700 text-white border-yellow-600',
          cancel:
            'bg-brand-white text-brand-gray-dark border-brand-gray-dark hover:bg-brand-gray-dark hover:text-brand-white',
        };
      case 'info':
        return {
          confirm: 'bg-blue-600 hover:bg-blue-700 text-white border-blue-600',
          cancel:
            'bg-brand-white text-brand-gray-dark border-brand-gray-dark hover:bg-brand-gray-dark hover:text-brand-white',
        };
      default:
        return {
          confirm: 'bg-brand-orange hover:bg-orange-600 text-white border-brand-orange',
          cancel:
            'bg-brand-white text-brand-gray-dark border-brand-gray-dark hover:bg-brand-gray-dark hover:text-brand-white',
        };
    }
  };

  const buttonStyles = getButtonStyles();

  const handleConfirm = () => {
    dialog.onConfirm();
    onClose();
  };

  const handleCancel = () => {
    if (dialog.onCancel) {
      dialog.onCancel();
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop con efecto focal */}
      <div className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal centrado */}
      <div className="relative bg-brand-white rounded-lg shadow-2xl max-w-md w-full mx-4 transform transition-all duration-300 ease-out scale-100 opacity-100">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            {getIcon()}
            <h3 className="text-lg font-semibold text-brand-gray-dark sentence-case">
              {dialog.title}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
            aria-label="Cerrar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-brand-gray-dark sentence-case leading-relaxed">{dialog.message}</p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={handleCancel}
            className={`px-4 py-2 rounded-md border transition-colors duration-200 sentence-case ${buttonStyles.cancel}`}
          >
            {dialog.cancelText || 'Cancelar'}
          </button>
          <button
            onClick={handleConfirm}
            className={`px-4 py-2 rounded-md border transition-colors duration-200 sentence-case ${buttonStyles.confirm}`}
          >
            {dialog.confirmText || 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  );
};
